/**
 * Harbour HTTP client. Three GETs (pulse, work, needs-you), then
 * on-demand search / drawer. Mutations take venueId. Same axios `api2`
 * pattern as Catch Board.
 */

import { API } from './interceptor'
import { HARBOUR_ERROR_CODE } from 'src/constants/harbour'
import {
  buildHarbourBoardQueryParams,
  buildHarbourPulseQueryParams,
  mapHarbourBoard,
  mapHarbourDrawer,
  mapHarbourMutationResult,
  mapHarbourOllieSuggestion,
  mapHarbourPulse,
  mapHarbourSearch,
} from './harbourMappers'

const HARBOUR_BASE = 'harbour'

function throwHarbourError(response, fallbackMessage) {
  const message =
    response?.message ||
    response?.data?.message ||
    fallbackMessage ||
    'Harbour request failed'
  const error = new Error(message)
  error.code = response?.code || response?.data?.code || 'HARBOUR_ERROR'
  error.status = response?.status
  if (
    response?.alreadyTaken ||
    error.code === HARBOUR_ERROR_CODE.ALREADY_TAKEN
  ) {
    error.alreadyTaken = true
    error.ownerName = response?.ownerName || response?.data?.ownerName || null
    error.venueName = response?.venueName || response?.data?.venueName || null
  }
  throw error
}

function assertHarbourSuccess(response, fallbackMessage) {
  if (!response?.success) {
    throwHarbourError(response, fallbackMessage)
  }
  return response.data
}

async function harbourRequest(request, fallbackMessage) {
  try {
    const response = await request()
    return assertHarbourSuccess(response, fallbackMessage)
  } catch (error) {
    if (error?.code && error?.message && !error?.response) {
      throw error
    }

    const payload = error?.response?.data
    if (payload && typeof payload === 'object') {
      throwHarbourError(
        {
          ...payload,
          status: error.response?.status || payload.status,
        },
        fallbackMessage,
      )
    }

    throwHarbourError(
      { message: error?.message, status: error?.response?.status },
      fallbackMessage,
    )
  }
}

export async function fetchHarbourPulse(filters = {}) {
  const data = await harbourRequest(
    () =>
      API.get('api2', `${HARBOUR_BASE}/pulse`, {
        queryStringParameters: buildHarbourPulseQueryParams(filters),
      }),
    'Failed to load Harbour pulse',
  )
  return mapHarbourPulse(data)
}

export async function fetchHarbourWork(filters = {}) {
  const data = await harbourRequest(
    () =>
      API.get('api2', `${HARBOUR_BASE}/work`, {
        queryStringParameters: buildHarbourBoardQueryParams(filters),
      }),
    'Failed to load Harbour work',
  )
  return mapHarbourBoard(data)
}

export async function fetchHarbourNeedsYou(filters = {}) {
  const data = await harbourRequest(
    () =>
      API.get('api2', `${HARBOUR_BASE}/needs-you`, {
        queryStringParameters: buildHarbourBoardQueryParams(filters),
      }),
    'Failed to load Harbour Needs you',
  )
  return mapHarbourBoard(data)
}

export async function searchHarbourVenues({ search, gap } = {}) {
  const queryStringParameters = {}
  const trimmed = String(search || '').trim()
  if (trimmed) {
    queryStringParameters.search = trimmed
  }
  if (gap) {
    queryStringParameters.gap = gap
  }
  const data = await harbourRequest(
    () =>
      API.get('api2', `${HARBOUR_BASE}/search`, {
        queryStringParameters,
      }),
    'Failed to search Harbour venues',
  )
  return mapHarbourSearch(data)
}

export async function fetchHarbourVenueDrawer(venueId) {
  const data = await harbourRequest(
    () => API.get('api2', `${HARBOUR_BASE}/venues/${venueId}`),
    'Failed to load Harbour venue',
  )
  return mapHarbourDrawer(data)
}

export async function startHarbourWork(body = {}) {
  const data = await harbourRequest(
    () =>
      API.post('api2', `${HARBOUR_BASE}/start`, {
        body,
      }),
    'Failed to start Harbour work',
  )
  return mapHarbourMutationResult(data)
}

export async function logHarbourAction(body = {}) {
  const data = await harbourRequest(
    () =>
      API.post('api2', `${HARBOUR_BASE}/log`, {
        body,
      }),
    'Failed to log Harbour action',
  )
  return mapHarbourMutationResult(data)
}

export async function snoozeHarbourCase(body = {}) {
  const data = await harbourRequest(
    () =>
      API.post('api2', `${HARBOUR_BASE}/snooze`, {
        body,
      }),
    'Failed to snooze Harbour case',
  )
  return mapHarbourMutationResult(data)
}

export async function parkHarbourCase(body = {}) {
  const data = await harbourRequest(
    () =>
      API.post('api2', `${HARBOUR_BASE}/park`, {
        body,
      }),
    'Failed to park Harbour case',
  )
  return mapHarbourMutationResult(data)
}

export async function undoHarbourOfframp(body = {}) {
  const data = await harbourRequest(
    () =>
      API.post('api2', `${HARBOUR_BASE}/undo`, {
        body,
      }),
    'Failed to undo Harbour action',
  )
  return mapHarbourMutationResult(data)
}

export async function handoffHarbourCase(body = {}) {
  const data = await harbourRequest(
    () =>
      API.post('api2', `${HARBOUR_BASE}/handoff`, {
        body,
      }),
    'Failed to hand off Harbour case',
  )
  return mapHarbourMutationResult(data)
}

export async function reassignHarbourCase(body = {}) {
  const data = await harbourRequest(
    () =>
      API.post('api2', `${HARBOUR_BASE}/reassign`, {
        body,
      }),
    'Failed to reassign Harbour case',
  )
  return mapHarbourMutationResult(data)
}

export async function suggestHarbourOllieNextStep(body = {}) {
  const data = await harbourRequest(
    () =>
      API.post('api2', `${HARBOUR_BASE}/suggest`, {
        body,
      }),
    'Failed to suggest Harbour next step',
  )
  return mapHarbourOllieSuggestion(data)
}
